// UserManagement.js
import React, { useEffect, useState } from "react";
import "./userManagement.css";
import { API_CONFIG, getApiUrl } from "./apiConfig";

// Helper function to compress image
const compressImage = (file, maxSizeMB = 1) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions (max 1920px width)
        const maxWidth = 1920;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels to meet size requirement
        let quality = 0.8;
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (blob.size <= maxSizeMB * 1024 * 1024 || quality <= 0.1) {
                // Create new File object with compressed blob
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                quality -= 0.1;
                tryCompress();
              }
            },
            'image/jpeg',
            quality
          );
        };
        tryCompress();
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

function Userlist() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    password: "", // New password field
    gender: "",
    birthdate: "",
    state: "",
    foodPreference: "",
    role: "", // New role field (will store role ID)
    photo: null,
    idProof: null,
    photoUrl: "", // To store existing photo URL
    idProofUrl: "" // To store existing ID proof URL
  });

  // Preview URLs for newly selected files
  const [photoPreview, setPhotoPreview] = useState("");
  const [idProofPreview, setIdProofPreview] = useState("");

  // Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // Static lookup tables - with GUIDs from backend
  const genderOptions = [
    { id: "293bc0d5-ff50-4626-830d-0f9cc08a31e1", code: "F", name: "Female" },
    { id: "C7EABC7E-9A90-4F5B-B5DE-A78BF4BBAAC2", code: "M", name: "Male" },
    { id: "9D16E81C-DFD1-4773-BEAA-AA6146C4C2E5", code: "O", name: "Other" }
  ];

  const foodPreferenceOptions = [
    { id: "AA6AE8A5-E43E-474E-952A-65850AEB790C", code: "NV", name: "Non-Vegetarian" },
    { id: "E24A36F1-1BAC-40F5-A3D4-73596FAEA3C4", code: "VEG", name: "Vegetarian" },
    { id: "45DD9A6A-3E33-4703-BD2C-F0C5C0966AD2", code: "JAIN", name: "Jain" }
  ];

  const participateTypes = [
    "Player",
    "Coach",
    "Team Manager",
    "Physio",
    "Support Staff",
    "BAI Office Bearers"
  ];

  // Helper functions to convert between IDs and codes
  const getGenderIdFromCode = (code) => {
    if (!code) return null;
    const gender = genderOptions.find(g => g.code === code);
    return gender ? gender.id : null;
  };

  const getGenderCodeFromId = (id) => {
    if (!id || id === "NULL" || id === "null") return "";
    const idUpper = id.toString().toUpperCase().trim();
    const gender = genderOptions.find(g => g.id.toUpperCase().trim() === idUpper);
    return gender ? gender.code : "";
  };

  const getFoodPrefIdFromCode = (code) => {
    if (!code) return null;
    const foodPref = foodPreferenceOptions.find(f => f.code === code);
    return foodPref ? foodPref.id : null;
  };

  const getFoodPrefCodeFromId = (id) => {
    if (!id || id === "NULL" || id === "null") return "";
    const idUpper = id.toString().toUpperCase().trim();
    const foodPref = foodPreferenceOptions.find(f => f.id.toUpperCase().trim() === idUpper);
    return foodPref ? foodPref.code : "";
  };

  // Helper function to format date to yyyy-MM-dd
  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return "";

    try {
      // Handle different date formats
      let date;

      // If it's already in yyyy-MM-dd format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }

      // If it has time component (ISO format)
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }

      // Format to yyyy-MM-dd
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Fetch roles from API
  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = localStorage.getItem("Token") || localStorage.getItem("token");

      if (!token) {
        console.error("No authentication token found for fetching roles");
        alert("No authentication token found. Please login again.");
        setLoadingRoles(false);
        return;
      }

      console.log("Fetching roles from API...");

      const response = await fetch(
        "https://api.asterickx.com/api/Roles",
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to fetch roles: ${response.status}`);
        console.error("Error response:", errorText);

        // Show user-friendly error
        alert(`Failed to load roles. Status: ${response.status}. Please try refreshing the page.`);
        setLoadingRoles(false);
        return;
      }

      const rolesData = await response.json();
      console.log("=== ROLES API RESPONSE ===");
      console.log("Raw roles data:", rolesData);
      console.log("Type:", typeof rolesData);
      console.log("Is Array:", Array.isArray(rolesData));
      console.log("Length:", rolesData?.length);

      if (rolesData && rolesData.length > 0) {
        console.log("First role object:", rolesData[0]);
        console.log("Keys in first role:", Object.keys(rolesData[0]));
      }

      // Check if it's wrapped in a data property
      const actualRolesData = rolesData.data || rolesData.Data || rolesData;

      if (!Array.isArray(actualRolesData)) {
        console.error("Roles data is not an array:", actualRolesData);
        alert("Invalid roles data format received from API");
        setLoadingRoles(false);
        return;
      }

      // Transform the API response to match the expected format
      const transformedRoles = actualRolesData.map((role, index) => {
        console.log(`Processing role ${index}:`, role);

        const id = (role.id || role.Id || role.roleId || role.RoleId || "").toString().toUpperCase();
        const code = role.code || role.Code || role.roleCode || role.RoleCode || "";
        // Use DisplayName for the dropdown display, fallback to other fields if not available
        const name = role.displayName || role.DisplayName || role.name || role.Name || role.roleName || role.RoleName || "";

        console.log(`Transformed role ${index}:`, { id, code, name });

        return { id, code, name };
      });

      console.log("All transformed roles:", transformedRoles);
      console.log("Number of roles:", transformedRoles.length);
      console.log("========================\n");

      if (transformedRoles.length === 0) {
        console.warn("No roles found in API response");
        alert("No roles available. Please contact administrator.");
      }

      setRoleOptions(transformedRoles);
    } catch (err) {
      console.error("Error fetching roles:", err);
      console.error("Error details:", err.message);
      alert(`Error loading roles: ${err.message}. Please try refreshing the page.`);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles(); // Fetch roles on component mount
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      const filtered = users.filter(user => {
        const firstNameMatch = user.firstName?.toLowerCase().includes(searchLower);
        const lastNameMatch = user.lastName?.toLowerCase().includes(searchLower);
        const emailMatch = user.email?.toLowerCase().includes(searchLower);
        const phoneMatch = user.mobileNumber?.includes(searchTerm.trim());

        return firstNameMatch || lastNameMatch || emailMatch || phoneMatch;
      });

      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("Token") || localStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await fetch(
        getApiUrl(API_CONFIG.ENDPOINTS.MEMBERS),
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          setError("Unauthorized. Your session may have expired. Please login again.");
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return;
      }

      const data = await response.json();
      setUsers(data);
      setFilteredUsers(data);
      setError("");
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message || "Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const { name } = e.target;
    const file = e.target.files[0];

    if (!file) return;

    try {
      let processedFile = file;

      // Check if it's an image file (not PDF)
      if (file.type.startsWith('image/')) {
        // Check file size (limit to 5MB before compression)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes

        if (file.size > maxSize) {
          // Show compression message
          const proceed = window.confirm(
            `File size is ${(file.size / 1024 / 1024).toFixed(2)}MB which is too large.\n\n` +
            `Would you like to automatically compress the image?\n\n` +
            `(This will reduce the file size while maintaining good quality)`
          );

          if (!proceed) {
            e.target.value = ''; // Clear the input
            return;
          }

          // Compress the image
          try {
            console.log(`Compressing ${name}...`);
            processedFile = await compressImage(file, 2); // Compress to max 2MB
            console.log(`Compressed from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
            alert(`Image compressed successfully!\nOriginal: ${(file.size / 1024 / 1024).toFixed(2)}MB → Compressed: ${(processedFile.size / 1024 / 1024).toFixed(2)}MB`);
          } catch (error) {
            console.error('Compression failed:', error);
            alert('Failed to compress image. Please try a smaller file.');
            e.target.value = '';
            return;
          }
        }
      }

      // Create preview URL for the selected/compressed file
      const previewUrl = URL.createObjectURL(processedFile);

      if (name === "photo") {
        // Revoke old preview URL if it exists
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoPreview(previewUrl);
      } else if (name === "idProof") {
        // Revoke old preview URL if it exists
        if (idProofPreview) URL.revokeObjectURL(idProofPreview);
        setIdProofPreview(previewUrl);
      }

      setFormData(prev => ({
        ...prev,
        [name]: processedFile
      }));
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please try again.');
      e.target.value = '';
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const resetUserPassword = async (mobileNumber, newPassword) => {
    try {
      const token = localStorage.getItem("Token") || localStorage.getItem("token");

      if (!token) {
        console.error("No token found for password reset");
        return;
      }

      console.log("Resetting password for:", mobileNumber);

      const response = await fetch(
        "https://api.asterickx.com/api/Auth/admin-reset-password",
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            mobileNumber: mobileNumber,  // Changed from phoneNumber to mobileNumber
            newPassword: newPassword
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Password reset failed:", errorData);
        throw new Error("Failed to reset password");
      }

      console.log("Password reset successfully");
    } catch (err) {
      console.error("Error resetting password:", err);
      // Don't fail the whole operation if password reset fails
      alert("User saved but password reset failed. Please try resetting password manually.");
    }
  };

  const assignRoleToUser = async (userId, roleId) => {
    try {
      const token = localStorage.getItem("Token") || localStorage.getItem("token");

      if (!token) {
        console.error("No token found for role assignment");
        return;
      }

      console.log("=== ASSIGNING ROLE ===");
      console.log("userId:", userId);
      console.log("roleId:", roleId);

      const payload = {
        userId: userId,
        roleId: roleId
      };

      console.log("Payload:", JSON.stringify(payload, null, 2));

      // Try multiple endpoint variations
      const endpoints = [
        "https://api.asterickx.com/api/UserRoles",
        "https://api.asterickx.com/api/Members/AssignRole",
        `https://api.asterickx.com/api/Members/${userId}/role`,
        "https://api.asterickx.com/api/User/AssignRole"
      ];

      let success = false;
      let lastError = null;

      for (const endpoint of endpoints) {
        console.log(`Trying endpoint: ${endpoint}`);

        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
          });

          console.log(`Response status: ${response.status}`);

          if (response.ok) {
            const responseData = await response.json().catch(() => null);
            console.log("Role assignment response:", responseData);
            console.log("✅ Role assigned successfully using:", endpoint);
            success = true;
            break;
          } else if (response.status === 404) {
            console.log("404 - Endpoint not found, trying next...");
            continue;
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error("Error response:", errorData);
            lastError = errorData;
          }
        } catch (err) {
          console.error(`Error with endpoint ${endpoint}:`, err.message);
          lastError = err;
        }
      }

      if (!success) {
        console.warn("⚠️ All role assignment endpoints returned 404");
        console.warn("The role assignment API may not be implemented yet.");
        console.warn("Last error:", lastError);
        // Don't throw error - just warn and continue
        console.log("User created successfully, but role was not assigned.");
        console.log("Role can be assigned manually later through the admin panel.");
      }

    } catch (err) {
      console.error("❌ Error in role assignment process:", err);
      // Don't fail the whole operation - user is already created
      console.log("Continuing despite role assignment failure...");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem("Token") || localStorage.getItem("token");

      if (!token) {
        alert("No authentication token found. Please login again.");
        return;
      }

      const submitData = new FormData();

      // Add all form fields, converting codes to IDs for gender and foodPreference
      Object.keys(formData).forEach(key => {
        // Skip role - it will be assigned separately via UserRoles API
        // Skip photoUrl and idProofUrl - they're only for display
        if (key === "role" || key === "photoUrl" || key === "idProofUrl") {
          return;
        }

        // Skip password in edit mode - will use admin reset password API if provided
        if (key === "password" && isEdit) {
          return;
        }

        if (formData[key] !== null && formData[key] !== "") {
          if (key === "gender") {
            const genderId = getGenderIdFromCode(formData[key]);
            if (genderId) {
              submitData.append("GenderId", genderId);
            }
          } else if (key === "foodPreference") {
            const foodPrefId = getFoodPrefIdFromCode(formData[key]);
            if (foodPrefId) {
              submitData.append("FoodPreferenceId", foodPrefId);
            }
          } else if (key === "birthdate") {
            // Ensure birthdate is in yyyy-MM-dd format
            const dateValue = formData[key];
            console.log("=== BIRTHDATE DEBUG ===");
            console.log("Raw birthdate value:", dateValue);
            console.log("Type:", typeof dateValue);
            console.log("Is empty?:", !dateValue);

            if (dateValue) {
              const formattedDate = formatDateToYYYYMMDD(dateValue);
              console.log("After formatting:", formattedDate);

              // Try multiple field name variations
              // Most likely: BirthDate with capital D (C# PascalCase standard)
              submitData.append("BirthDate", formattedDate);    // Capital D - MOST LIKELY
              submitData.append("Birthdate", formattedDate);     // Lowercase d
              submitData.append("DateOfBirth", formattedDate);   // Full name
              submitData.append("birthdate", formattedDate);     // All lowercase
              submitData.append("DOB", formattedDate);           // Abbreviation

              console.log("Sent as: BirthDate, Birthdate, DateOfBirth, birthdate, DOB");
              console.log("======================\n");
            } else {
              console.log("Birthdate is empty, skipping");
              console.log("======================\n");
            }
          } else {
            // Convert field names to PascalCase for backend
            const fieldNameMap = {
              'firstName': 'FirstName',
              'lastName': 'LastName',
              'mobileNumber': 'MobileNumber',
              'email': 'Email',
              'password': 'Password',
              'state': 'State',
              'photo': 'Photo',
              'idProof': 'IdProof'
            };

            const backendFieldName = fieldNameMap[key] || key;
            submitData.append(backendFieldName, formData[key]);
          }
        }
      });

      // Debug logging
      console.log("=== SUBMIT DEBUG ===");
      console.log("Is Edit:", isEdit);
      console.log("Edit User ID:", editUserId);
      console.log("Role to assign:", formData.role);
      console.log("Password:", formData.password ? (isEdit ? "Will use admin reset API" : "Included in Members API") : "No password change");
      console.log("\nFormData contents:");
      for (let pair of submitData.entries()) {
        if (pair[1] instanceof File) {
          console.log(`${pair[0]}: [File] ${pair[1].name} (${(pair[1].size / 1024).toFixed(2)}KB)`);
        } else {
          console.log(`${pair[0]}: ${pair[1]}`);
        }
      }
      console.log("===================\n");

      const url = isEdit
        ? `${getApiUrl(API_CONFIG.ENDPOINTS.MEMBERS)}/${editUserId}`
        : getApiUrl(API_CONFIG.ENDPOINTS.MEMBERS);

      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: submitData
      });

      if (!response.ok) {
        if (response.status === 401) {
          alert("Unauthorized. Please login again.");
          return;
        } else if (response.status === 413) {
          alert("File size too large! Please reduce the size of your images and try again. Maximum total size allowed is 10MB.");
          return;
        } else if (response.status === 400) {
          const errorData = await response.json().catch(() => ({}));
          console.error("400 Error Response:", errorData);

          // Try to extract meaningful error message
          let errorMessage = "Bad request. Please check all fields are filled correctly.";

          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.errors) {
            // Handle validation errors (object of errors)
            const errors = Object.values(errorData.errors).flat();
            errorMessage = errors.join('\n');
          } else if (errorData.title) {
            errorMessage = errorData.title;
          }

          alert(`Error: ${errorMessage}`);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} user`);
      }

      // Clean up preview URLs
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      if (idProofPreview) URL.revokeObjectURL(idProofPreview);

      // Get the user ID from response
      let userId = editUserId; // Default to edit user ID

      if (!isEdit) {
        // Only parse response for new user creation
        try {
          const responseData = await response.json();
          console.log("=== USER CREATION RESPONSE ===");
          console.log("Full response:", responseData);
          console.log("response.id:", responseData.id);
          console.log("response.Id:", responseData.Id);
          console.log("response.data?.id:", responseData.data?.id);
          console.log("response.birthdate:", responseData.birthdate);
          console.log("response.Birthdate:", responseData.Birthdate);
          console.log("response.dateOfBirth:", responseData.dateOfBirth);
          console.log("response.DateOfBirth:", responseData.DateOfBirth);
          console.log("=============================\n");

          userId = responseData.id || responseData.Id || responseData.data?.id || responseData.data?.Id;
          console.log("Extracted user ID:", userId);
        } catch (parseError) {
          console.error("Failed to parse response:", parseError);
          // If we can't get ID, still try to continue
        }
      }

      // Reset password only in EDIT mode if password is provided
      // (In add mode, password is already sent to Members API)
      console.log("=== PASSWORD RESET CHECK ===");
      console.log("isEdit:", isEdit);
      console.log("formData.password:", formData.password ? "Yes" : "No");
      console.log("formData.mobileNumber:", formData.mobileNumber);
      console.log("Should reset password:", isEdit && formData.password && formData.mobileNumber);
      console.log("===========================\n");

      if (isEdit && formData.password && formData.mobileNumber) {
        console.log("Calling resetUserPassword...");
        await resetUserPassword(formData.mobileNumber, formData.password);
      } else {
        console.log("Skipping password reset (not in edit mode or no password provided)");
      }

      // Assign role if role is selected and we have a user ID
      console.log("=== ROLE ASSIGNMENT CHECK ===");
      console.log("formData.role:", formData.role);
      console.log("userId:", userId);
      console.log("Will assign role:", formData.role && userId);
      console.log("============================\n");

      if (formData.role && userId) {
        await assignRoleToUser(userId, formData.role);
      } else {
        if (!formData.role) {
          console.warn("No role selected - skipping role assignment");
        }
        if (!userId) {
          console.error("No userId available - cannot assign role");
        }
      }

      resetForm();
      fetchUsers();
      alert(`User ${isEdit ? 'updated' : 'created'} successfully!`);
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} user:`, err);
      alert(err.message || `Failed to ${isEdit ? 'update' : 'create'} user. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("Token") || localStorage.getItem("token");

      if (!token) {
        alert("No authentication token found. Please login again.");
        return null;
      }

      const response = await fetch(
        `${getApiUrl(API_CONFIG.ENDPOINTS.MEMBERS)}/${userId}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          alert("Unauthorized. Please login again.");
          return null;
        }
        throw new Error(`Failed to fetch user details: ${response.status}`);
      }

      const userData = await response.json();
      return userData;
    } catch (err) {
      console.error("Error fetching user details:", err);
      alert("Failed to load user details. Please try again.");
      return null;
    }
  };

  const handleEdit = async (user) => {
    setIsEdit(true);
    setEditUserId(user.id);

    // Get authentication token
    const token = localStorage.getItem("Token") || localStorage.getItem("token");

    if (!token) {
      alert("No authentication token found. Please login again.");
      return;
    }

    // Clear any previous file previews
    setPhotoPreview("");
    setIdProofPreview("");

    // Show modal with loading state
    setShowModal(true);
    setSubmitting(true);

    // Fetch complete user details from API
    const completeUserData = await fetchUserDetails(user.id);

    setSubmitting(false);

    if (completeUserData) {
      // Parse birthdate - check multiple possible field names (case-insensitive)
      let birthdateValue = "";
      const birthdateField = completeUserData.birthdate ||
        completeUserData.dateOfBirth ||
        completeUserData.Birthdate ||
        completeUserData.DateOfBirth ||
        completeUserData.dob ||
        completeUserData.DOB;

      if (birthdateField) {
        birthdateValue = formatDateToYYYYMMDD(birthdateField);
        console.log("Birthdate from API:", birthdateField);
        console.log("Formatted birthdate:", birthdateValue);
      }

      // Extract gender code from GenderId (try both lowercase and uppercase)
      let genderCode = "";
      const genderId = completeUserData.genderId ||
        completeUserData.GenderId ||
        completeUserData.gender ||
        completeUserData.Gender;

      if (genderId) {
        genderCode = getGenderCodeFromId(genderId);
      }

      // Extract food preference code from FoodPreferenceId (try all possible field names)
      let foodPrefCode = "";
      const foodPrefId = completeUserData.foodPreferenceId ||
        completeUserData.FoodPreferenceId ||
        completeUserData.foodPreference ||
        completeUserData.FoodPreference;

      if (foodPrefId) {
        foodPrefCode = getFoodPrefCodeFromId(foodPrefId);
      }

      // Construct full image URLs by prepending base URL (without /api path)
      // Images are served from root, not from /api endpoint
      const apiBaseUrl = API_CONFIG.BASE_URL || "https://api.asterickx.com/api";
      const baseUrl = apiBaseUrl.replace(/\/api\/?$/, ""); // Remove /api from end
      const photoPath = completeUserData.photoPath || completeUserData.PhotoPath || "";
      const idProofPath = completeUserData.idProofPath || completeUserData.IdProofPath || "";

      const fullPhotoUrl = photoPath ? `${baseUrl}${photoPath}` : "";
      const fullIdProofUrl = idProofPath ? `${baseUrl}${idProofPath}` : "";

      console.log("=== IMAGE URL DEBUG ===");
      console.log("API Base URL:", apiBaseUrl);
      console.log("Base URL (for images):", baseUrl);
      console.log("Photo Path from API:", photoPath);
      console.log("ID Proof Path from API:", idProofPath);
      console.log("Full Photo URL:", fullPhotoUrl);
      console.log("Full ID Proof URL:", fullIdProofUrl);
      console.log("=====================\n");

      // Fetch user's current role
      let userRoleId = "";
      try {
        const roleResponse = await fetch(
          `https://api.asterickx.com/api/UserRoles?userId=${user.id}`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        if (roleResponse.ok) {
          const roleData = await roleResponse.json();
          console.log("=== ROLE FETCH DEBUG ===");
          console.log("Raw role data:", roleData);
          console.log("Is array:", Array.isArray(roleData));
          console.log("Length:", roleData?.length);
          if (roleData && roleData.length > 0) {
            console.log("First role object:", roleData[0]);
            console.log("roleId field:", roleData[0].roleId);
            console.log("RoleId field:", roleData[0].RoleId);
            // Normalize to uppercase for consistent matching
            const fetchedRoleId = roleData[0].roleId || roleData[0].RoleId || "";
            userRoleId = fetchedRoleId.toUpperCase();
            console.log("Extracted userRoleId (normalized):", userRoleId);
          } else {
            console.log("No role data in response");
          }
          console.log("=======================\n");
        } else {
          console.error("Role fetch failed with status:", roleResponse.status);
        }
      } catch (err) {
        console.error("Error fetching user role:", err);
      }

      console.log("=== SETTING FORM DATA ===");
      console.log("Setting role to:", userRoleId);
      console.log("Gender code:", genderCode);
      console.log("Food pref code:", foodPrefCode);
      console.log("Birthdate:", birthdateValue);
      console.log("========================\n");

      setFormData({
        firstName: completeUserData.firstName || completeUserData.FirstName || "",
        lastName: completeUserData.lastName || completeUserData.LastName || "",
        mobileNumber: completeUserData.mobileNumber || completeUserData.MobileNumber || "",
        email: completeUserData.email || completeUserData.Email || "",
        password: "", // Don't populate password in edit mode
        gender: genderCode,
        birthdate: birthdateValue,
        state: completeUserData.state || completeUserData.State || "",
        foodPreference: foodPrefCode,
        role: userRoleId, // Set the user's current role
        photo: null,
        idProof: null,
        photoUrl: fullPhotoUrl,
        idProofUrl: fullIdProofUrl
      });
    } else {
      // Fallback to data from table if API call fails
      let birthdateValue = "";
      const birthdateField = user.birthdate || user.dateOfBirth || user.Birthdate || user.DateOfBirth;
      if (birthdateField) {
        birthdateValue = formatDateToYYYYMMDD(birthdateField);
      }

      const genderId = user.genderId || user.GenderId || user.gender || user.Gender;
      const foodPrefId = user.foodPreferenceId || user.FoodPreferenceId || user.foodPreference || user.FoodPreference;

      let genderCode = genderId ? getGenderCodeFromId(genderId) : "";
      let foodPrefCode = foodPrefId ? getFoodPrefCodeFromId(foodPrefId) : "";

      // Construct full image URLs by prepending base URL (without /api path)
      const apiBaseUrl = API_CONFIG.BASE_URL || "https://api.asterickx.com/api";
      const baseUrl = apiBaseUrl.replace(/\/api\/?$/, ""); // Remove /api from end
      const photoPath = user.photoPath || user.PhotoPath || "";
      const idProofPath = user.idProofPath || user.IdProofPath || "";

      setFormData({
        firstName: user.firstName || user.FirstName || "",
        lastName: user.lastName || user.LastName || "",
        mobileNumber: user.mobileNumber || user.MobileNumber || "",
        email: user.email || user.Email || "",
        password: "", // Don't populate password in edit mode
        gender: genderCode,
        birthdate: birthdateValue,
        state: user.state || user.State || "",
        foodPreference: foodPrefCode,
        role: "", // Will be populated by getUserRole if available
        photo: null,
        idProof: null,
        photoUrl: photoPath ? `${baseUrl}${photoPath}` : "",
        idProofUrl: idProofPath ? `${baseUrl}${idProofPath}` : ""
      });
    }
  };

  const resetForm = () => {
    // Revoke object URLs to free memory
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    if (idProofPreview) URL.revokeObjectURL(idProofPreview);

    setFormData({
      firstName: "",
      lastName: "",
      mobileNumber: "",
      email: "",
      password: "",
      gender: "",
      birthdate: "",
      state: "",
      foodPreference: "",
      role: "",
      photo: null,
      idProof: null,
      photoUrl: "",
      idProofUrl: ""
    });
    setPhotoPreview("");
    setIdProofPreview("");
    setShowPassword(false); // Reset password visibility
    setShowModal(false);
    setIsEdit(false);
    setEditUserId(null);
  };

  return (
    <div className="user-management-container">
      <div className="header-section">
        <h2 className="page-title">User Management</h2>

        <div className="right-controls">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <svg
              className="search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>

          <button
            className="add-user-btn"
            onClick={() => {
              setIsEdit(false);
              resetForm();
              setShowModal(true);
            }}
          >
            <span className="btn-icon">+</span>
            <span>Add New User</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p className="error-message">{error}</p>
          {error.includes("login") && (
            <button
              className="retry-btn"
              onClick={() => window.location.href = "/login"}
            >
              Go to Login
            </button>
          )}
        </div>
      )}

      {searchTerm && (
        <div className="search-results-info">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      )}

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Phone Number</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center">
                  <div className="loader"></div>
                  Loading...
                </td>
              </tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user, index) => (
                <tr key={user.id || index}>
                  <td>{user.firstName || "N/A"}</td>
                  <td>{user.lastName || "N/A"}</td>
                  <td>{user.email || "N/A"}</td>
                  <td>{user.mobileNumber || "N/A"}</td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => handleEdit(user)}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  {searchTerm ? "No users found matching your search" : "No users available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === "modal-overlay") {
            resetForm();
          }
        }}>
          <div className="modal-container">
            <div className="modal-header-custom">
              <h3>{isEdit ? "Edit User" : "Add New User"}</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>

            <div className="modal-body-custom">
              {submitting && isEdit && !formData.firstName ? (
                <div className="loading-user-details">
                  <div className="loader"></div>
                  <p>Loading user details...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter first name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter last name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Mobile Number *</label>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter mobile number"
                      />
                    </div>

                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter email address"
                      />
                    </div>

                    <div className="form-group">
                      <label>Password *</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required={!isEdit}
                          placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                          minLength="6"
                          className="password-input"
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          )}
                        </button>
                      </div>
                      {isEdit && (
                        <small className="field-hint">Leave blank to keep current password</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Gender *</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        {genderOptions.map((option) => (
                          <option key={option.id} value={option.code}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Birthdate *</label>
                      <input
                        type="date"
                        name="birthdate"
                        value={formData.birthdate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>From State *</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter state"
                      />
                    </div>

                    <div className="form-group">
                      <label>Food Preference *</label>
                      <select
                        name="foodPreference"
                        value={formData.foodPreference}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Food Preference</option>
                        {foodPreferenceOptions.map((option) => (
                          <option key={option.id} value={option.code}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Role *</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        required
                        disabled={loadingRoles}
                      >
                        <option value="">
                          {loadingRoles ? "Loading roles..." : "Select Role"}
                        </option>
                        {roleOptions.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      {loadingRoles && (
                        <small className="field-hint">Fetching available roles...</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Photo {isEdit ? "(Leave blank to keep current)" : "*"}</label>

                      {/* Existing Photo */}
                      {isEdit && formData.photoUrl && !photoPreview && (
                        <div className="image-preview">
                          <img
                            src={formData.photoUrl}
                            alt="Current photo"
                            className="preview-thumbnail"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.textContent = "Image not available";
                            }}
                          />
                          <span className="preview-label">Current Photo</span>
                        </div>
                      )}

                      {/* New Preview */}
                      {photoPreview && (
                        <div className="image-preview">
                          <img src={photoPreview} alt="New photo preview" className="preview-thumbnail" />
                          <div className="preview-info">
                            <span className="preview-label">New Photo (Preview)</span>
                            {formData.photo && (
                              <span className="file-size">
                                Size: {(formData.photo.size / 1024).toFixed(0)} KB
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Upload Button */}
                      <label className="upload-btn">
                        
                        <input
                          type="file"
                          name="photo"
                          accept="image/*"
                          onChange={handleFileChange}
                          required={!isEdit}
                          className="hidden-file"
                        />
                      </label>

                      <small className="field-hint">Maximum size: 5MB. Large images will be auto-compressed.</small>
                    </div>


                    <div className="form-group">
                      <label>ID Proof {isEdit ? "(Leave blank to keep current)" : "*"}</label>

                      {/* Existing ID Proof */}
                      {isEdit && formData.idProofUrl && !idProofPreview && (
                        <div className="image-preview">
                          <img
                            src={formData.idProofUrl}
                            alt="Current ID proof"
                            className="preview-thumbnail"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.textContent = "Image not available";
                            }}
                          />
                          <span className="preview-label">Current ID Proof</span>
                        </div>
                      )}

                      {/* New Preview */}
                      {idProofPreview && (
                        <div className="image-preview">
                          <img
                            src={idProofPreview}
                            alt="New ID proof preview"
                            className="preview-thumbnail"
                          />
                          <div className="preview-info">
                            <span className="preview-label">New ID Proof (Preview)</span>
                            {formData.idProof && (
                              <span className="file-size">
                                Size: {(formData.idProof.size / 1024).toFixed(0)} KB
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Upload Button */}
                      <label className="upload-btn">
                        
                        <input
                          type="file"
                          name="idProof"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                          required={!isEdit}
                          className="hidden-file"
                        />
                      </label>

                      <small className="field-hint">Maximum size: 5MB. Large images will be auto-compressed.</small>
                    </div>

                  </div>

                  <div className="form-actions">
                    <button type="button" className="cancel-btn" onClick={resetForm}>
                      Cancel
                    </button>
                    <button type="submit" className="submit-btn" disabled={submitting}>
                      {submitting ? "Processing..." : (isEdit ? "Update User" : "Create User")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Userlist;